/**
 * OCR API Client
 * Centralized API client for all OCR-related operations
 */

import type {
  OCRTaskStatus,
  OCRResult,
  OCRUploadResponse,
  BatchOCRResponse,
  PaginatedOCRResults,
  ExtractedFieldsResponse,
  OCRResultsFilters,
  OCRUploadRequest,
  BatchOCRRequest,
} from '@/types/ocr';
import { fetchWithAuthRetry } from '@/lib/auth/fetch-with-auth-retry';

const AI_SERVICE_URL = process.env.NEXT_PUBLIC_AI_SERVICE_URL || 'http://localhost:8103';

/**
 * Get auth token from localStorage. May return null if the access token
 * happens to be expired/cleared at this exact moment -- callers no longer
 * treat that as fatal (see authHeader() below): the request still goes out,
 * and fetchWithAuthRetry transparently refreshes + retries on the 401 that
 * comes back, exactly like every other API client in this app. Previously
 * every function here threw "Authentication required" synchronously right
 * here, before even attempting a request, which meant a session that was
 * perfectly recoverable via the refresh token looked like a hard logout the
 * instant this modal's Auto-Extract/Save ran mid-token-expiry.
 */
function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('access_token') || localStorage.getItem('super_admin_token');
}

function authHeader(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Build query string from filters
 */
function buildQueryString(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  return params.toString();
}

/**
 * Upload file(s) for OCR processing
 */
export async function uploadForOCR(
  files: File[],
  options: OCRUploadRequest
): Promise<OCRUploadResponse> {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  // Add options as JSON string
  formData.append('document_type', options.document_type);
  if (options.detection_type) {
    formData.append('detection_type', options.detection_type);
  }
  if (options.job_id) {
    formData.append('job_id', options.job_id);
  }
  if (options.ai_request_id) {
    formData.append('ai_request_id', options.ai_request_id);
  }

  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/upload`, {
    method: 'POST',
    headers: authHeader(),
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Failed to upload document');
  }

  return response.json();
}

/**
 * Process OCR for existing image URL
 */
export async function processExistingImage(
  imageUrl: string,
  options: OCRUploadRequest
): Promise<OCRUploadResponse> {
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/process`, {
    method: 'POST',
    headers: {
      ...authHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      image_url: imageUrl,
      ...options,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Processing failed' }));
    throw new Error(error.detail || 'Failed to process image');
  }

  return response.json();
}

/**
 * Get OCR task status
 */
export async function getTaskStatus(taskId: string): Promise<OCRTaskStatus> {
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/status/${taskId}`, {
    method: 'GET',
    headers: authHeader(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get status' }));
    throw new Error(error.detail || 'Failed to get task status');
  }

  return response.json();
}

/**
 * Get OCR result by ID
 */
export async function getOCRResult(resultId: string): Promise<OCRResult> {
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/results/${resultId}`, {
    method: 'GET',
    headers: authHeader(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get result' }));
    throw new Error(error.detail || 'Failed to get OCR result');
  }

  return response.json();
}

/**
 * Get paginated list of OCR results
 */
export async function getOCRResults(
  filters: OCRResultsFilters = {}
): Promise<PaginatedOCRResults> {
  const queryString = buildQueryString({
    page: filters.page || 1,
    page_size: filters.page_size || 20,
    document_type: filters.document_type,
    status: filters.status,
    date_from: filters.date_from,
    date_to: filters.date_to,
    search: filters.search,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
  });

  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/results?${queryString}`, {
    method: 'GET',
    headers: authHeader(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get results' }));
    throw new Error(error.detail || 'Failed to get OCR results');
  }

  return response.json();
}

/**
 * Get extracted fields for an OCR result
 */
export async function getExtractedFields(resultId: string): Promise<ExtractedFieldsResponse> {
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/results/${resultId}/fields`, {
    method: 'GET',
    headers: authHeader(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to get fields' }));
    throw new Error(error.detail || 'Failed to get extracted fields');
  }

  return response.json();
}

/**
 * Reprocess field extraction for an OCR result
 */
export async function reprocessExtraction(resultId: string): Promise<{ message: string }> {
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/reprocess-extraction`, {
    method: 'POST',
    headers: {
      ...authHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ocr_result_id: resultId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Reprocessing failed' }));
    throw new Error(error.detail || 'Failed to reprocess extraction');
  }

  return response.json();
}

/**
 * Batch process multiple images
 */
export async function batchProcessOCR(request: BatchOCRRequest): Promise<BatchOCRResponse> {
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/batch`, {
    method: 'POST',
    headers: {
      ...authHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Batch processing failed' }));
    throw new Error(error.detail || 'Failed to batch process images');
  }

  return response.json();
}

/**
 * Download JSON file from S3 URL
 */
export async function downloadJSON(url: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to download JSON file');
  }
  return response.json();
}

/**
 * Cancel a running OCR task (if supported by backend)
 */
export async function cancelTask(taskId: string): Promise<{ message: string }> {
  // Note: This endpoint may not exist in the backend yet
  const response = await fetchWithAuthRetry(`${AI_SERVICE_URL}/api/v1/ai/ocr/cancel/${taskId}`, {
    method: 'POST',
    headers: authHeader(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Cancellation failed' }));
    throw new Error(error.detail || 'Failed to cancel task');
  }

  return response.json();
}

/**
 * OCR API Client object (for easier imports)
 */
export const ocrApi = {
  upload: uploadForOCR,
  processExisting: processExistingImage,
  getStatus: getTaskStatus,
  getResult: getOCRResult,
  getResults: getOCRResults,
  getFields: getExtractedFields,
  reprocess: reprocessExtraction,
  batchProcess: batchProcessOCR,
  downloadJSON,
  cancelTask,
};

export default ocrApi;
